
import React, { useEffect } from 'react';
import { blogPosts } from '@/data/blog-posts';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ScrollToTop from '@/components/ScrollToTop';
import { Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatDate } from '@/lib/utils';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '@/components/ui/pagination';
import SEO from '@/components/SEO';

const POSTS_PER_PAGE = 10;

const Blog = () => {
  const [currentPage, setCurrentPage] = React.useState(1);
  const totalPages = Math.ceil(blogPosts.length / POSTS_PER_PAGE);
  
  const startIndex = (currentPage - 1) * POSTS_PER_PAGE;
  const displayedPosts = blogPosts.slice(startIndex, startIndex + POSTS_PER_PAGE);
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  return (
    <div className="flex flex-col min-h-screen bg-dark-bg">
      <SEO
        title="SkyTest.ai Blog"
        description="Insights, best practices, and thought leadership on QA automation, AI-assisted testing, and ensuring quality software delivery"
      />
      <Navbar />
      
      <main className="flex-grow pt-24 pb-20 px-1.5 md:px-6 lg:px-12">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-gradient">
              SkyTest.ai Blog
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Insights, best practices, and thought leadership on QA automation, 
              AI-assisted testing, and ensuring quality software delivery
            </p>
          </div>
          
          <div className="space-y-12">
            {displayedPosts.map((post) => (
              <Link 
                key={post.slug}
                to={`/blog/${post.slug}`} 
                className="block glass-morphism rounded-xl overflow-hidden hover:shadow-brand-sm transition-all duration-300"
              >
                <div className="md:flex">
                  <div className="md:w-1/3 h-[200px] md:h-auto overflow-hidden">
                    <img 
                      src={post.thumbnail} 
                      alt={post.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500 ease-in-out"
                    />
                  </div>
                  
                  <div className="p-6 md:w-2/3">
                    <div className="flex items-center text-sm text-gray-400 mb-3">
                      <Calendar size={14} className="mr-1" />
                      <time dateTime={post.date}>{formatDate(post.date)}</time>
                    </div>
                    
                    <h2 className="text-2xl font-bold text-white mb-3 hover:text-blue-50 transition-default">
                      {post.title}
                    </h2>
                    
                    <p className="text-gray-300">
                      {post.summary}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-12">
              <Pagination>
                <PaginationContent>
                  {currentPage > 1 && (
                    <PaginationItem>
                      <PaginationPrevious 
                        onClick={() => handlePageChange(currentPage - 1)}
                        className="cursor-pointer"
                      />
                    </PaginationItem>
                  )}
                  
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <PaginationItem key={i}>
                      <PaginationLink
                        isActive={currentPage === i + 1}
                        onClick={() => handlePageChange(i + 1)}
                        className="cursor-pointer"
                      >
                        {i + 1}
                      </PaginationLink>
                    </PaginationItem>
                  ))}
                  
                  {currentPage < totalPages && (
                    <PaginationItem>
                      <PaginationNext 
                        onClick={() => handlePageChange(currentPage + 1)}
                        className="cursor-pointer"
                      />
                    </PaginationItem>
                  )}
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </div>
      </main>
      
      <Footer />
      <ScrollToTop />
    </div>
  );
};

export default Blog;
